/**
 * Bloc de texte enrichi — widget de mise en page, sans données ni requête.
 *
 * Deux modes :
 *  - **lecture** (défaut, et seul mode en consultation/export) : le HTML persisté est
 *    assaini par `sanitizeRichText` puis inséré via `dangerouslySetInnerHTML` ;
 *  - **édition** : `TipTapEditor`, activé quand la page passe `editable` + `onChange`
 *    (le contenu remonte alors dans `config.display.content_html`).
 *
 * Le contenu est toujours assaini au rendu, jamais seulement à l'enregistrement :
 * un contenu déjà en base (antérieur, importé, modifié hors UI) reste neutralisé.
 */
import type { WidgetConfig } from '../schemas';
import { sanitizeRichText } from '../sanitizeRichText';
import TipTapEditor, { EDITOR_CONTENT_CLASS } from './TipTapEditor';

type Row = Record<string, string | number>;

interface WidgetProps {
  config: WidgetConfig;
  data: { current: Row[]; previous: Row[] | null; meta: any } | null;
  loading?: boolean;
  error?: string;
  /** Mode édition (builder). Sans `onChange`, le bloc reste en lecture seule. */
  editable?: boolean;
  onChange?: (html: string) => void;
}

// Cadre fourni par `WidgetShell` (WidgetRenderer) : ici, padding + hauteur + scroll.
const SHELL = 'h-full overflow-auto';

/** Styles du HTML rendu en lecture — miroir de ceux de l'éditeur, sans le préfixe ProseMirror. */
const READONLY_CONTENT_CLASS = [
  'text-sm text-[#1B2559]',
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-[#1B2559]',
  '[&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-[#1B2559]',
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-[#1B2559]',
  '[&_p]:my-1',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-[#E2E4ED] [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-[#8B8FA7]',
  '[&_code]:bg-[#F4F5FA] [&_code]:rounded [&_code]:px-1 [&_code]:text-xs',
  '[&_pre]:bg-[#F4F5FA] [&_pre]:rounded [&_pre]:p-3 [&_pre]:my-2 [&_pre]:overflow-x-auto',
  '[&_a]:text-[#6366F1] [&_a]:underline',
].join(' ');

export default function RichTextBlockWidget({ config, loading, error, editable, onChange }: WidgetProps) {
  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const display = (config.display ?? {}) as Record<string, unknown>;
  const contentHtml = (display.content_html as string | undefined) ?? '';

  if (editable && onChange) {
    return (
      // `rich-text-editable` : sélecteur d'exclusion du drag react-grid-layout
      // (`dragConfig.cancel` dans PageCanvas). Sans lui, le mousedown dans
      // l'éditeur démarre le drag de la grille et le curseur ne peut pas être posé.
      <div className={`${SHELL} p-0 rich-text-editable`}>
        <TipTapEditor
          contentHtml={contentHtml}
          onChange={onChange}
          editable
          placeholder="Cliquez pour éditer ce bloc de texte"
        />
      </div>
    );
  }

  const safeHtml = sanitizeRichText(contentHtml).trim();

  if (!safeHtml || safeHtml === '<p></p>') {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7] italic">Bloc de texte vide</span>
      </div>
    );
  }

  return (
    <div className={SHELL}>
      {/* Contenu assaini par `sanitizeRichText` (liste blanche de balises/attributs). */}
      <div className={READONLY_CONTENT_CLASS} dangerouslySetInnerHTML={{ __html: safeHtml }} />
    </div>
  );
}
